import React, { useState, useEffect } from "react";
import {
	Accordion,
	AccordionItem,
	Avatar,
	Chip,
	Listbox,
	ListboxItem,
} from "@heroui/react";

function BoxTab({ box }) {
	if (!box || box.length === 0) {
		return (
			<div className="flex flex-col items-center justify-center h-48 text-xs text-default-400">
				暂无盲盒开盒数据
			</div>
		);
	}
	const itemClasses = {
		base: "py-0 w-full",
		titleWrapper: "flex-1 min-w-0",
		title: "font-normal text-medium w-full",
		subtitle: "text-[12px] text-foreground font-normal",
		trigger:
			"px-2.5 py-0 data-[hover=true]:bg-default dark:data-[hover=true]:bg-default-50/40 rounded-xl h-14 flex items-center",
		indicator: "text-medium",
		content: "text-small px-0 pb-1",
	};

	return (
		<Accordion
			className="p-1 flex flex-col gap-1 w-full"
			itemClasses={itemClasses}
			showDivider={false}
			variant="light"
			selectionMode={"multiple"}
		>
			{box.map((item) => {
				const spend = Number(item.spend) || 0;
				const totalRet = Number(item.return) || 0;
				const rate =
					spend > 0 ? ((totalRet / spend) * 100).toFixed(0) : "0";
				const isOverallProfit = spend > 0 && totalRet >= spend;
				const avatarBase =
					(typeof AVATAR_API !== "undefined"
						? AVATAR_API
						: typeof window !== "undefined"
							? window.AVATAR_API
							: "") || "https://workers.vrp.moe/bilibili/avatar/";

				return (
					<AccordionItem
						key={item.uid}
						aria-label={item.u_name || "box-item"}
						startContent={
							<Avatar
								src={avatarBase + item.uid}
								size="sm"
								className="shrink-0"
							/>
						}
						title={
							<div className="flex items-center justify-between gap-2 w-full">
								<div className="flex flex-col min-w-0">
									<span className="font-bold text-sm truncate">
										{item.u_name || `主播 (${item.uid})`}
									</span>
									<span className="text-[12px] text-foreground flex items-center gap-1 font-normal">
										<span>
											成本 ¥{spend.toLocaleString()}
										</span>
										<span>=&gt;</span>
										<span>
											回报 ¥
											{totalRet.toLocaleString(
												undefined,
												{ maximumFractionDigits: 1 },
											)}
										</span>
									</span>
								</div>
								<Chip
									size="sm"
									variant="flat"
									color={
										isOverallProfit ? "success" : "danger"
									}
									className="h-7 px-2.5 border-0 shrink-0 font-bold"
								>
									<div className="flex items-center gap-1">
										{isOverallProfit ? (
											<svg
												className="w-3.5 h-3.5 shrink-0"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.5"
												strokeLinecap="round"
												strokeLinejoin="round"
												viewBox="0 0 24 24"
											>
												<path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
											</svg>
										) : (
											<svg
												className="w-3.5 h-3.5 shrink-0"
												fill="none"
												stroke="currentColor"
												strokeWidth="2.5"
												strokeLinecap="round"
												strokeLinejoin="round"
												viewBox="0 0 24 24"
											>
												<path d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" />
											</svg>
										)}
										<span className="text-xs tabular-nums leading-none">
											{`${rate}%`}
										</span>
									</div>
								</Chip>
							</div>
						}
					>
						<Listbox aria-label="盲盒掉落排行" className="p-0">
							{item.top.map((boxItem, idx) => {
								const parts = (boxItem.box || "").split(",");
								const boxName = parts[0] || "盲盒";
								const singleBoxPrice = Number(parts[1]) || 0;
								const count = Number(boxItem.gift_amount) || 1;
								const totalSpend = singleBoxPrice * count;
								const totalReturn =
									Number(boxItem.gift_price) || 0;
								const isProfit = totalReturn >= totalSpend;
								const diff =
									Math.round(
										(totalReturn - totalSpend) * 10,
									) / 10;
								const giftName =
									boxItem.gift_name || "未知礼物";
								const giftImg =
									GIFT_MAP.get(giftName) ||
									GIFT_MAP.get(giftName.trim());

								return (
									<ListboxItem
										key={boxItem.id || idx}
										textValue={`${boxName} ${giftName} ${totalReturn}`}
										className="py-2 px-2.5 rounded-lg cursor-default"
										startContent={
											<div className="flex items-center gap-2.5 min-w-0">
												<Avatar
													src={giftImg}
													name={giftName}
													radius="md"
													size="sm"
													className="w-8 h-8 shrink-0 bg-transparent text-[10px]"
													fallback={
														<span className="text-sm">
															🎁
														</span>
													}
												/>

												<div className="flex flex-col min-w-0">
													<div className="flex items-center gap-1.5 min-w-0">
														<span className="text-xs font-semibold text-foreground truncate">
															{giftName}
														</span>
														{count > 1 && (
															<span className="text-[10px] text-default-500 shrink-0 font-medium">
																×{count}
															</span>
														)}
													</div>
													<div className="flex items-center gap-1 text-[11px] text-default-400">
														<span className="truncate max-w-[120px]">
															{boxName}
														</span>
														<span className="line-through">
															¥
															{totalSpend.toLocaleString()}
														</span>
														{count > 1 && (
															<span className="text-[10px] text-default-400">
																(单价¥
																{singleBoxPrice}
																)
															</span>
														)}
													</div>
												</div>
											</div>
										}
										endContent={
											<div className="flex flex-col items-end justify-center shrink-0">
												<div className="flex items-center gap-1.5">
													<span
														className={`text-xs font-semibold ${
															isProfit
																? "text-success"
																: "text-default-700"
														}`}
													>
														¥
														{totalReturn.toLocaleString()}
													</span>
													<Chip
														size="sm"
														variant="flat"
														color={
															isProfit
																? "success"
																: "danger"
														}
														className="h-5 text-[10px] px-1.5 font-medium border-0 shrink-0"
													>
														{isProfit
															? `+¥${diff.toLocaleString()}`
															: `-¥${Math.abs(diff).toLocaleString()}`}
													</Chip>
												</div>
												<span className="text-[10px] text-default-400 mt-0.5">
													{new Date(
														boxItem.created_at,
													).toLocaleDateString()}
												</span>
											</div>
										}
									>
										{null}
									</ListboxItem>
								);
							})}
						</Listbox>
					</AccordionItem>
				);
			})}
		</Accordion>
	);
}

export default BoxTab;
